import type { MenuItem } from '@/types/menu'

/**
 * 侧栏导航的纯数据层：把后端 menuTree（role→menu 驱动）整形成
 * 保留目录树，同时维护用于路由、收藏与徽标的叶子索引，
 * 并提供搜索过滤 / 路由激活判断等无副作用的辅助函数。
 * 与渲染解耦，便于单测。
 */

export interface NavGroup {
  /** 分组 key：目录菜单的 SnowflakeId 字符串；无标题的散落组为 'ungrouped' */
  id: string
  label: string
  /** 后端目录菜单的多语言名(en/es/pt/zh)；缺则回退 label */
  labelI18n?: Record<string, string>
  /** 后端目录菜单的 icon 字段（可能为空，渲染层再做 label 兜底映射） */
  icon: string | null
  /** Platform 组带 Admin 角标（可见性本身由后端权限过滤，这里只管 UI 提示） */
  adminOnly: boolean
  items: MenuItem[]
  /** Preserved directory tree for rendering; items remains the leaf index. */
  tree?: MenuItem[]
}

export const normalizePath = (path: string): string => {
  if (!path) return ''
  const normalized = path.replace(/^\/+|\/+$/g, '')
  return normalized ? `/${normalized}` : '/'
}

/**
 * 组级 badge 汇总：组内所有子项按 routePath(normalize 后)查 menuBadges,
 * 有计数的全部加总——与具体是哪个 badge key 无关。用于组标题(收起态)展示
 * 汇总计数,新增一个子项 badge自动被汇总进去,
 * 不需要在这里逐个列举 key。
 */
export const sumGroupBadges = (
  items: MenuItem[],
  menuBadges: Record<string, number | undefined>,
): number =>
  items.reduce((sum, it) => {
    const key = it.routePath ? normalizePath(it.routePath) : null
    return key ? sum + (menuBadges[key] ?? 0) : sum
  }, 0)

/** 菜单路由是否匹配当前路径（精确或前缀，前缀按路径段对齐，避免 /task 误吞 /task-calendar） */
export const isPathActive = (menuPath: string, currentPath: string): boolean => {
  const target = normalizePath(menuPath)
  if (!target) return false
  if (currentPath === target) return true
  if (target !== '/' && currentPath.startsWith(`${target}/`)) return true
  return false
}

const getSortOrder = (item: MenuItem): number =>
  item.sortOrder ?? (item as unknown as { sort_order?: number }).sort_order ?? 0

const sortMenuTree = (items: MenuItem[]): MenuItem[] =>
  [...items]
    // Retired page: hide stale server menu entries, including saved shortcuts.
    .filter(item => !item.routePath || !isPathActive("/concierge-requests", normalizePath(item.routePath)))
    .sort((a, b) => getSortOrder(a) - getSortOrder(b))
    .map((item) => ({
      ...item,
      children: item.children ? sortMenuTree(item.children) : undefined,
    }))

/** Leaf index shared by route matching, badges and pinned shortcuts. */
const flattenItems = (items: MenuItem[]): MenuItem[] =>
  items.flatMap((item) => {
    if (item.type === 1 && item.children && item.children.length > 0) {
      return flattenItems(item.children)
    }
    if (item.type === 1) return []
    return [item]
  })

const pruneDirectories = (items: MenuItem[]): MenuItem[] => items.flatMap((item) => {
  if (item.type !== 1) return [item]
  const children = pruneDirectories(item.children ?? [])
  return children.length ? [{ ...item, children }] : []
})

const matchesMenu = (item: MenuItem, q: string): boolean =>
  item.name.toLowerCase().includes(q) || Object.values(item.labelI18n ?? {}).some((s) => typeof s === 'string' && s.toLowerCase().includes(q))

const filterTree = (items: MenuItem[], q: string): MenuItem[] => items.flatMap((item) => {
  if (matchesMenu(item, q)) return [item]
  const children = filterTree(item.children ?? [], q)
  return children.length ? [{ ...item, children }] : []
})

const PLATFORM_GROUP_LABEL = 'platform'

/**
 * menuTree → NavGroup[]。
 * 顶层目录（type=1 且有子项）成组；顶层散落的菜单项归入开头的 'ungrouped' 组；
 * 空目录跳过。排序遵循后端 sortOrder（与 Access Control 一致）。
 */
export const deriveNavGroups = (menuTree: MenuItem[] | null | undefined): NavGroup[] => {
  if (!menuTree || menuTree.length === 0) return []

  const sorted = sortMenuTree(menuTree)
  const groups: NavGroup[] = []
  let ungrouped: NavGroup | null = null

  sorted.forEach((item) => {
    if (item.type === 1) {
      const items = flattenItems(item.children ?? [])
      if (items.length === 0) return
      groups.push({
        id: String(item.id),
        label: item.name,
        labelI18n: item.labelI18n,
        icon: item.icon || null,
        adminOnly: item.name.trim().toLowerCase() === PLATFORM_GROUP_LABEL,
        items,
        tree: pruneDirectories(item.children ?? []),
      })
    } else {
      if (!ungrouped) {
        ungrouped = { id: 'ungrouped', label: '', icon: null, adminOnly: false, items: [] }
        groups.push(ungrouped)
      }
      ungrouped.items.push(item)
    }
  })

  return groups
}

/**
 * 搜索过滤：不区分大小写的子串匹配，只保留有命中的分组。
 * query 为空时原样返回（引用不变，方便 memo）。
 */
export const filterNavGroups = (groups: NavGroup[], query: string): NavGroup[] => {
  const q = query.trim().toLowerCase()
  if (!q) return groups
  return groups
    .map((g) => {
      if (!g.tree) return { ...g, items: g.items.filter((item) => matchesMenu(item, q)) }
      const tree = filterTree(g.tree, q)
      return { ...g, tree, items: flattenItems(tree) }
    })
    .filter((g) => g.items.length > 0)
}

/** 当前路径所在的分组 id（rail 初始展开 / 未展开组的选中提示用） */
export const findActiveGroupId = (groups: NavGroup[], currentPath: string): string | null => {
  for (const g of groups) {
    if (g.items.some((it) => isPathActive(it.routePath || '', currentPath))) return g.id
  }
  return null
}

// ── 展开态单组手风琴（Frank 2026-06-12 简化双组方案）──
// 滚动区任何时刻最多展开一个组：点谁开谁、其他全关；再点展开中的组收起全部。
// 当前页所在组被关掉时，标题显示琥珀小圆点提示「选中项在这组里」。

/** 点击分组标题：开着→全收起(null)；关着→只开它（其他自然全关） */
export const reduceHeadClick = (openGroupId: string | null, groupId: string): string | null =>
  openGroupId === groupId ? null : groupId

/** 滚动区分组是否展开：搜索强制展开 ∥ 它是唯一展开组 */
export const isGroupOpen = (
  groupId: string,
  openGroupId: string | null,
  searching: boolean,
): boolean => searching || groupId === openGroupId

/** active 组被关掉时显示琥珀小圆点 */
export const showActiveDot = (
  groupId: string,
  openGroupId: string | null,
  activeGroupId: string | null,
  searching: boolean,
): boolean => !searching && groupId === activeGroupId && groupId !== openGroupId

/** 当前路径命中的具体条目（最长路由优先，避免前缀路由抢选中态）。
 *  侧栏选中态用 id，顶栏标题用整项取 labelI18n —— 共用同一匹配逻辑。 */
export const findActiveItem = (groups: NavGroup[], currentPath: string): MenuItem | null => {
  let best: { item: MenuItem; len: number } | null = null
  for (const g of groups) {
    for (const it of g.items) {
      const target = normalizePath(it.routePath || '')
      if (!target || !isPathActive(target, currentPath)) continue
      if (!best || target.length > best.len) best = { item: it, len: target.length }
    }
  }
  return best ? best.item : null
}

/** 当前路径命中的具体条目 id（最长路由优先，避免前缀路由抢选中态） */
export const findActiveItemId = (groups: NavGroup[], currentPath: string): string | null => {
  const item = findActiveItem(groups, currentPath)
  return item ? String(item.id) : null
}

/**
 * 按 pinnedIds 顺序，从非 My Workspace 分组(restGroups)的叶子项里解析出镜像 MenuItem。
 * 查不到的 id(角色变更/菜单下架)静默丢弃；按 id 去重。数据源不含 Workspace,
 * 因此天然排除其自身的项。纯函数、无副作用,便于单测与 memo。
 */
export const resolvePinnedItems = (
  restGroups: NavGroup[],
  pinnedIds: Array<MenuItem['id']>,
): MenuItem[] => {
  const byId = new Map<string, MenuItem>()
  for (const g of restGroups) {
    for (const it of g.items) byId.set(String(it.id), it)
  }
  const seen = new Set<string>()
  const out: MenuItem[] = []
  for (const id of pinnedIds) {
    const key = String(id)
    if (seen.has(key)) continue
    const item = byId.get(key)
    if (item) {
      out.push(item)
      seen.add(key)
    }
  }
  return out
}
